/**
 * Shared brand assets — the single source for the shared origin and the URLs built
 * from it.
 *
 * Brand assets (fonts, icons, images) live in the shared bucket and are served from
 * that environment's website host, NOT from the PWA host — see frontend/CLAUDE.md.
 * They must therefore be referenced absolutely. A relative `/images/…` does not merely
 * 404 on the PWA host: that path is not rewritten to the shared bucket, so it falls
 * through to the SPA catch-all and returns index.html with a **200**, and the `<img>`
 * fails silently with no failing status to notice.
 *
 * `/icons/*` is the exception — the URL map rewrites it to the PWA's own build output
 * (`pwa/icons/*`), so app icons such as the manifest set stay relative and same-origin.
 *
 * Cross-origin loads are governed by CSP `img-src` / `font-src`, which list this origin
 * explicitly. Anything added here has to stay within that allow-list.
 */

/** Origin serving the shared bucket. CI sets this per environment; the default keeps
 *  local dev and any missing-env build pointing at production. */
export const SHARED_ORIGIN = import.meta.env.VITE_SHARED_ORIGIN ?? 'https://plantmewallet.com';

/** Absolute URL for a shared-bucket asset. `path` is root-relative, e.g. `/images/x.webp`. */
export function sharedAsset(path: string): string {
  return `${SHARED_ORIGIN}${path}`;
}

/** The still brand mascot — the masthead mark. */
export const LOGO_URL = sharedAsset('/images/plantme-wallet-logo.webp');

/** The animated brand mascot, reserved for the welcome splash. Much heavier than the
 *  still mark, so it is deliberately not used for small, always-on chrome. */
export const LOGO_ANIMATED_URL = sharedAsset('/images/plantme-wallet-animated-logo.webp');
